import React, { useContext, useMemo } from 'react';
import { createUseStyles } from 'react-jss';
import { ColorBadge, Theme, OfferShiftOriginalIcon, OfferShiftReallocatedIcon } from '@spoiler-alert/ui-library';
import VTable from './v-table/v-table';
import ScrollContext from '../../layouts/scroll-context';
import OfferShiftContext from './offer-shift-context';
import { doesInventoryRowMatchFilters, OfferCompFilters } from './offer-comparison-filter-operations';
import { OfferCompInventory, User } from '../../graphql/query-result-types';
import VColumn from './v-table/v-column';
import { DesirabilityIndicator, hasReservePriceExceptionOpportunity } from '../../components/desirability';
import { featureFlags } from '../../enums';
import { checkFeature } from '../../helpers';
import type { VTableType } from './v-table/v-table-types';

const CYPRESS_TAGS = {
  TABLE: 'offer-comparison-table',
};

const useStyles = createUseStyles(
  {
    badgeInner: {
      display: 'flex',
      alignItems: 'center',
      gap: 5,
    },
    shiftOriginalIcon: {
      fill: Theme.teal,
    },
    shiftReallocatedIcon: {
      fill: Theme.purple,
    },
  },
  { name: 'OfferComparisonTable' },
);

/**
 * Exported so the column's render rules can be tested without standing up the whole table -- the
 * trucklane drilldown exposes its own copy the same way.
 */
export const buildDesirabilityColumn = () =>
  new VColumn<OfferCompInventory>({
    field: 'desirability',
    displayName: '',
    visible: true,
    sortable: false,
    width: 40,
    resizable: true,
    // A low-desirability call is only worth surfacing here when there is something to do about it
    // -- i.e. the inventory has at least one offer suggested for a reserve price exception. Without
    // one the flag is noise in a column the supplier cannot act on.
    formatter: (_: unknown, row: OfferCompInventory) =>
      hasReservePriceExceptionOpportunity(row?.offerListings) ? (
        <DesirabilityIndicator desirability={row?.desirability} surface="offer-comparison" />
      ) : null,
  });

interface OfferComparisonTableProps {
  user: User;
  inventories: OfferCompInventory[];
  noDataMessage: string;
  handleRowClick: (row: OfferCompInventory) => void;
  searchText: string;
  filters: OfferCompFilters;
  selectedInventory: OfferCompInventory;
  filteredInventoryIds: React.MutableRefObject<string[]>;
  checkedInventoryIds: string[];
  handleCheck: (args: { value: string; checked: boolean; event: React.MouseEvent<HTMLDivElement> }) => void;
  loading: boolean;
}

const OfferComparisonTable: React.FC<OfferComparisonTableProps> = ({
  noDataMessage,
  handleRowClick,
  inventories,
  selectedInventory,
  searchText,
  filters,
  user,
  filteredInventoryIds,
  checkedInventoryIds = [],
  handleCheck,
  loading,
}) => {
  const classes = useStyles();
  const offerShiftContextValue = useContext(OfferShiftContext);
  const rowsWithMetadata = useMemo(() => {
    return inventories.map((inv) => {
      const incomingShiftCount = offerShiftContextValue?.shiftMap.get(inv._id)?.length || 0;
      let highestOfferPercentSuggestedPrice = null;
      let highestOfferTotalBidPrice = null;
      const highestOffer = inv.offerListings?.length > 0 ? [...inv.offerListings].sort((a, b) => b.totalPrice - a.totalPrice)[0] : null;
      if (highestOffer) {
        highestOfferTotalBidPrice = highestOffer.totalPrice;
        const unitPrice = highestOffer.totalPrice / highestOffer.quantity;
        highestOfferPercentSuggestedPrice = highestOffer.suggestedUnitPrice > 0 ? (unitPrice / highestOffer.suggestedUnitPrice) * 100 : null;
      }
      const row = {
        ...inv,
        offerListingCount: inv.offerListingCount + incomingShiftCount,
        highestOfferTotalBidPrice,
        highestOfferPercentSuggestedPrice,
      };
      return row;
    });
  }, [inventories, offerShiftContextValue?.shiftMap]);

  const filteredListings = useMemo(() => {
    const rows = rowsWithMetadata.filter((row) => doesInventoryRowMatchFilters(row, filters, offerShiftContextValue?.shiftMap));
    if (filteredInventoryIds) filteredInventoryIds.current = rows.map((row) => row._id);
    return rows;
  }, [rowsWithMetadata, filteredInventoryIds, filters, offerShiftContextValue?.shiftMap]);

  const { viewableListings, filtersOverridden } = useMemo(() => {
    if (offerShiftContextValue && offerShiftContextValue.currentShifts.length > 0) {
      const { currentShifts } = offerShiftContextValue;
      const shiftComponentIds = currentShifts.reduce(
        (sets, shift) => {
          sets.invIds.add(shift.shiftInventoryId);
          sets.olIds.add(shift.originalOfferListingId);
          return sets;
        },
        { invIds: new Set<string>(), olIds: new Set<string>() },
      );
      const listings = rowsWithMetadata.filter((inventory) => {
        if (shiftComponentIds.invIds.has(inventory._id)) return true;
        return inventory.offerListings.some((ol) => shiftComponentIds.olIds.has(ol._id));
      });
      if (filteredInventoryIds) filteredInventoryIds.current = listings.map((row) => row._id);
      return { viewableListings: listings, filtersOverridden: true };
    }
    return { viewableListings: filteredListings, filtersOverridden: false };
  }, [offerShiftContextValue, filteredListings, rowsWithMetadata, filteredInventoryIds]);

  const scrollContainer = useContext(ScrollContext);

  const columnOverrides = useMemo(() => {
    const overrides: { [key: string]: Partial<VColumn<OfferCompInventory>> } = {
      offerPerCase: {
        width: 158,
        resizable: false,
      },
    };
    if (offerShiftContextValue && offerShiftContextValue.currentShifts.length > 0) {
      const rowIndex = offerShiftContextValue.currentShifts.reduce((rowMap, shift) => {
        rowMap.set(
          shift.shiftInventoryId,
          <ColorBadge id={`reallocated-${shift._id}-${shift.shiftInventoryId}`} theme={'purple'}>
            <div className={classes.badgeInner}>
              <OfferShiftReallocatedIcon className={classes.shiftReallocatedIcon} /> Shifted Bid
            </div>
          </ColorBadge>,
        );
        rowMap.set(
          shift.originalOfferListing.inventoryId,
          <ColorBadge id={`original-${shift._id}`} theme={'teal'}>
            <div className={classes.badgeInner}>
              <OfferShiftOriginalIcon className={classes.shiftOriginalIcon} /> Original Bid
            </div>
          </ColorBadge>,
        );
        return rowMap;
      }, new Map<string, React.ReactNode>());
      overrides.offerPerCase = {
        displayName: 'Shift Details',
        width: 197,
        formatter: (_, row: OfferCompInventory) => {
          if (rowIndex.has(row._id)) return rowIndex.get(row._id);
        },
      };
    }
    return overrides;
  }, [offerShiftContextValue, classes]);

  const insertColumns = useMemo(() => {
    if (!checkFeature(featureFlags.lowDesirabilityFlag)) return [];
    return [{ before: 'description', column: buildDesirabilityColumn() }];
  }, []);

  const OfferCompVTable = VTable as VTableType<OfferCompInventory>;

  return (
    <OfferCompVTable
      data={viewableListings}
      loading={loading}
      profileName="Offer Comp"
      columnOverrides={columnOverrides}
      insertColumns={insertColumns}
      userSite={user.site}
      checkboxes
      visibleRowsRef={filteredInventoryIds}
      onRowClick={handleRowClick}
      highlightRowId={selectedInventory?._id}
      searchText={filtersOverridden ? '' : searchText}
      noDataMessage={noDataMessage}
      keyboardNavigation
      cypressTagTable={CYPRESS_TAGS.TABLE}
      checkedRows={checkedInventoryIds}
      onRowCheck={handleCheck}
      scrollContainer={scrollContainer?.current}
      sticky
      stickyOffset={45}
    />
  );
};

export default OfferComparisonTable;
